/**
 * Strategy Performance Page
 * Full analytics dashboard like TradetrON
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './StrategyPerformance.css';
import apiClient from '../api/apiClient';
import { 
  downloadCSV, 
  downloadXLS, 
  downloadJSON, 
  downloadPDF, 
  generatePDFContent,
  exportPerformanceData 
} from '../utils/exportUtils';

interface PerformanceData {
  strategy_id: string;
  name: string;
  creator_name: string;
  metrics: {
    total_return: number;
    win_rate: number;
    sharpe_ratio: number;
    max_drawdown: number;
    profit_factor: number;
    total_trades: number;
    sortino_ratio: number;
    calmar_ratio: number;
    avg_win: number;
    avg_loss: number;
  };
  equity_curve: Array<{ date: string; equity: number; pnl: number }>;
  drawdown: Array<{ date: string; drawdown: number }>;
  monthly_returns: Array<{
    month: string;
    trades: number;
    pnl: number;
    return_pct: number;
    win_rate: number;
  }>;
  benchmark_comparison: Record<string, {
    value: number;
    rating: string;
    benchmark: string;
  }>;
}

const BENCHMARKS = {
  win_rate: { poor: '<40%', avg: '40-50%', good: '50-60%', excellent: '>60%' },
  sharpe_ratio: { poor: '<0.5', avg: '0.5-1.0', good: '1.0-2.0', excellent: '>2.0' },
  max_drawdown: { poor: '>30%', avg: '20-30%', good: '10-20%', excellent: '<10%' },
  profit_factor: { poor: '<1.0', avg: '1.0-1.5', good: '1.5-2.0', excellent: '>2.0' },
};

export default function StrategyPerformance() {
  const { strategyId } = useParams<{ strategyId: string }>();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'equity' | 'monthly' | 'risk' | 'compare'>('equity');
  
  // A2: Time range state for equity curve
  const [timeRange, setTimeRange] = useState<'1M' | '3M' | '6M' | '1Y' | 'ALL'>('ALL');
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [showPnL, setShowPnL] = useState(true);

  useEffect(() => {
    async function fetchPerformance() {
      if (!strategyId) return;
      
      try {
        setLoading(true);
        const response = await apiClient.get(`/marketplace/strategy/${strategyId}/performance`);
        setData(response);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch performance:', err);
        setError('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    }
    
    fetchPerformance();
  }, [strategyId]);

  // A2: Filter equity data based on time range
  const getFilteredEquityData = () => {
    if (!data?.equity_curve) return [];
    
    const ranges: Record<string, number> = {
      '1M': 1, '3M': 3, '6M': 6, '1Y': 12, 'ALL': 12
    };
    const months = ranges[timeRange];
    
    return data.equity_curve.slice(-months);
  };

  // A2: Generate benchmark data (NIFTY comparison)
  const generateBenchmarkData = (equityData: typeof data.equity_curve) => {
    if (!showBenchmark || !equityData?.length) return [];
    
    // Simulate NIFTY benchmark (average ~12% annual return)
    const benchmarkStart = 100000;
    return equityData.map((point, i) => ({
      ...point,
      benchmark: benchmarkStart * (1 + 0.01 * (i + 1) * (0.8 + Math.random() * 0.4))
    }));
  };

  // A3: Calculate drawdown statistics
  const calculateDrawdownStats = () => {
    if (!drawdown.length) return null;
    
    const ddValues = drawdown.map(d => d.drawdown);
    const maxDrawdown = Math.min(...ddValues);
    const maxDrawdownDate = drawdown.find(d => d.drawdown === maxDrawdown)?.date || '';
    
    // Calculate drawdown duration (consecutive periods in drawdown)
    let inDrawdown = false;
    let currentDuration = 0;
    let maxDuration = 0;
    let totalDrawdownDays = 0;
    
    for (const d of ddValues) {
      if (d < 0) {
        inDrawdown = true;
        currentDuration++;
        totalDrawdownDays++;
      } else {
        if (inDrawdown) {
          maxDuration = Math.max(maxDuration, currentDuration);
          currentDuration = 0;
        }
        inDrawdown = false;
      }
    }
    maxDuration = Math.max(maxDuration, currentDuration);
    
    // Average drawdown
    const negativeDDs = ddValues.filter(d => d < 0);
    const avgDrawdown = negativeDDs.length > 0 
      ? negativeDDs.reduce((a, b) => a + b, 0) / negativeDDs.length 
      : 0;
    
    // Recovery time estimate (days to recover from max DD)
    const recoveryDays = Math.ceil(Math.abs(maxDrawdown) * 10); // Rough estimate
    
    return {
      maxDrawdown,
      maxDrawdownDate,
      maxDuration,
      avgDrawdown,
      totalDrawdownDays,
      recoveryDays,
      drawdownPeriods: ddValues.filter(d => d < 0).length
    };
  };

  const getRating = (value: number, type: 'higher' | 'lower') => {
    if (type === 'higher') {
      if (value >= 60) return { label: 'Excellent', color: '#10b981' };
      if (value >= 50) return { label: 'Good', color: '#22c55e' };
      if (value >= 40) return { label: 'Average', color: '#f59e0b' };
      return { label: 'Poor', color: '#ef4444' };
    } else {
      const absVal = Math.abs(value);
      if (absVal <= 10) return { label: 'Excellent', color: '#10b981' };
      if (absVal <= 20) return { label: 'Good', color: '#22c55e' };
      if (absVal <= 30) return { label: 'Average', color: '#f59e0b' };
      return { label: 'Poor', color: '#ef4444' };
    }
  };

  const getRiskRating = (value: number) => {
    if (value >= 2.0) return { label: 'Excellent', color: '#10b981' };
    if (value >= 1.5) return { label: 'Good', color: '#22c55e' };
    if (value >= 1.0) return { label: 'Average', color: '#f59e0b' };
    return { label: 'Poor', color: '#ef4444' };
  };

  const getBenchmarkColor = (rating: string) => {
    switch (rating) {
      case 'Excellent': return '#10b981';
      case 'Good': return '#22c55e';
      case 'Average': return '#f59e0b';
      default: return '#ef4444';
    }
  };

  if (loading) {
    return <div className="perf-loading">Loading performance data...</div>;
  }

  if (error || !data) {
    return (
      <div className="perf-error">
        <h2>⚠️ Error Loading Performance</h2>
        <p>{error || 'No data available'}</p>
        <Link to="/marketplace" className="back-link">← Back to Marketplace</Link>
      </div>
    );
  }

  const { metrics, equity_curve, drawdown, monthly_returns, benchmark_comparison } = data;

  return (
    <div className="strategy-performance">
      <div className="perf-header">
        <Link to="/marketplace" className="back-link">← Back to Marketplace</Link>
        <div className="perf-title">
          <h1>📊 {data.name}</h1>
          <span className="creator">by {data.creator_name}</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <span className="metric-label">Total Return</span>
          <span className={`metric-value ${metrics.total_return >= 0 ? 'positive' : 'negative'}`}>
            {metrics.total_return >= 0 ? '+' : ''}{metrics.total_return}%
          </span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Win Rate</span>
          <span className="metric-value" style={{ color: getRating(metrics.win_rate, 'higher').color }}>
            {metrics.win_rate}%
          </span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Sharpe Ratio</span>
          <span className="metric-value" style={{ color: getRiskRating(metrics.sharpe_ratio).color }}>
            {metrics.sharpe_ratio}
          </span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Max Drawdown</span>
          <span className="metric-value" style={{ color: getRating(Math.abs(metrics.max_drawdown), 'lower').color }}>
            {metrics.max_drawdown}%
          </span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Profit Factor</span>
          <span className="metric-value" style={{ color: getRiskRating(metrics.profit_factor).color }}>
            {metrics.profit_factor}
          </span>
        </div>
        <div className="metric-card">
          <span className="metric-label">Total Trades</span>
          <span className="metric-value">{metrics.total_trades}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="perf-tabs">
        <button className={activeTab === 'equity' ? 'active' : ''} onClick={() => setActiveTab('equity')}>
          📈 Equity Curve
        </button>
        <button className={activeTab === 'monthly' ? 'active' : ''} onClick={() => setActiveTab('monthly')}>
          📅 Monthly Returns
        </button>
        <button className={activeTab === 'risk' ? 'active' : ''} onClick={() => setActiveTab('risk')}>
          🎯 Risk Metrics
        </button>
        <button className={activeTab === 'compare' ? 'active' : ''} onClick={() => setActiveTab('compare')}>
          🏆 Benchmarks
        </button>
      </div>

      {/* A4: User Export Section */}
      {data && (
        <div className="user-export-section">
          <div className="user-export-header">
            <div className="user-export-info">
              <h3>📥 Export Your Strategy Report</h3>
              <p>Download your complete strategy performance in multiple formats for analysis, backup, or sharing.</p>
            </div>
            <div className="user-export-stats">
              <span className="user-stat">
                <strong>{data.monthly_returns?.length || 0}</strong> months
              </span>
              <span className="user-stat">
                <strong>{data.equity_curve?.length || 0}</strong> data points
              </span>
            </div>
          </div>
          
          <div className="user-export-formats">
            <button 
              onClick={() => exportPerformanceData.fullReport(data).csv()} 
              className="user-export-btn"
            >
              <span className="export-icon">📄</span>
              <span className="export-label">CSV</span>
              <span className="export-desc">Spreadsheet compatible</span>
            </button>
            
            <button 
              onClick={() => exportPerformanceData.fullReport(data).xls()} 
              className="user-export-btn primary"
            >
              <span className="export-icon">📊</span>
              <span className="export-label">Excel</span>
              <span className="export-desc">Multi-sheet workbook</span>
            </button>
            
            <button 
              onClick={() => exportPerformanceData.fullReport(data).json()} 
              className="user-export-btn"
            >
              <span className="export-icon">📋</span>
              <span className="export-label">JSON</span>
              <span className="export-desc">Structured data</span>
            </button>
            
            <button 
              onClick={() => downloadPDF(generatePDFContent(data), `strategy-${data.strategy_id}`)} 
              className="user-export-btn"
            >
              <span className="export-icon">📑</span>
              <span className="export-label">PDF</span>
              <span className="export-desc">Print-ready document</span>
            </button>
            
            <button 
              onClick={() => {
                const aiReport = {
                  type: "strategy_performance_report",
                  version: "6.2.0",
                  exported_at: new Date().toISOString(),
                  strategy: {
                    id: data.strategy_id,
                    name: data.name,
                    creator: data.creator_name
                  },
                  summary: {
                    total_return: data.metrics?.total_return,
                    win_rate: data.metrics?.win_rate,
                    sharpe_ratio: data.metrics?.sharpe_ratio,
                    max_drawdown: data.metrics?.max_drawdown,
                    profit_factor: data.metrics?.profit_factor,
                    total_trades: data.metrics?.total_trades,
                    sortino_ratio: data.metrics?.sortino_ratio,
                    calmar_ratio: data.metrics?.calmar_ratio,
                    avg_win: data.metrics?.avg_win,
                    avg_loss: data.metrics?.avg_loss
                  },
                  monthly_performance: {
                    total_months: data.monthly_returns?.length,
                    profitable_months: data.monthly_returns?.filter((m: any) => m.return_pct > 0).length,
                    losing_months: data.monthly_returns?.filter((m: any) => m.return_pct < 0).length,
                    best_month: Math.max(...(data.monthly_returns?.map((m: any) => m.return_pct) || [0])),
                    worst_month: Math.min(...(data.monthly_returns?.map((m: any) => m.return_pct) || [0])),
                    average_return: data.monthly_returns?.reduce((a: number, b: any) => a + b.return_pct, 0) / (data.monthly_returns?.length || 1),
                    total_pnl: data.monthly_returns?.reduce((a: number, b: any) => a + b.pnl, 0)
                  },
                  raw_data: {
                    equity_curve: data.equity_curve,
                    drawdown: data.drawdown,
                    monthly_returns: data.monthly_returns,
                    benchmark_comparison: data.benchmark_comparison
                  }
                };
                downloadJSON(aiReport, `strategy_report_${data.strategy_id}`);
              }} 
              className="user-export-btn ai"
            >
              <span className="export-icon">🤖</span>
              <span className="export-label">AI Report</span>
              <span className="export-desc">ML/AI analysis ready</span>
            </button>
          </div>

          <div className="user-export-note">
            <span>💡 Tip: The AI Report format is optimized for machine learning and AI model analysis with structured metadata.</span>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="perf-content">
        {activeTab === 'equity' && (
          <div className="chart-section">
            {/* A2: Time Range Selector */}
            <div className="chart-controls">
              <div className="time-range-selector">
                <span className="control-label">Time Range:</span>
                {(['1M', '3M', '6M', '1Y', 'ALL'] as const).map((range) => (
                  <button
                    key={range}
                    className={`range-btn ${timeRange === range ? 'active' : ''}`}
                    onClick={() => setTimeRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>
              <div className="chart-toggles">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={showBenchmark}
                    onChange={(e) => setShowBenchmark(e.target.checked)}
                  />
                  <span>📊 NIFTY Benchmark</span>
                </label>
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={showPnL}
                    onChange={(e) => setShowPnL(e.target.checked)}
                  />
                  <span>💰 Show P&L Line</span>
                </label>
              </div>
            </div>

            {/* A4: Export Controls for Equity Curve */}
            <div className="section-export-bar">
              <span className="section-title">Equity Curve Data</span>
              <div className="export-buttons">
                <button onClick={() => exportPerformanceData.equityCurve(getFilteredEquityData()).csv()} className="export-btn-sm">📄 CSV</button>
                <button onClick={() => exportPerformanceData.equityCurve(getFilteredEquityData()).xls()} className="export-btn-sm">📊 XLS</button>
                <button onClick={() => exportPerformanceData.equityCurve(getFilteredEquityData()).json()} className="export-btn-sm">📋 JSON</button>
                <button onClick={() => downloadPDF(`<h2>Equity Curve</h2><pre>${JSON.stringify(getFilteredEquityData(), null, 2)}</pre>`, 'equity-curve')} className="export-btn-sm">📑 PDF</button>
                <button onClick={() => {
                  const aiData = { type: "equity_curve", strategy_id: data?.strategy_id, data: getFilteredEquityData(), exported_at: new Date().toISOString() };
                  downloadJSON(aiData, `ai_equity_${data?.strategy_id}`);
                }} className="export-btn-sm ai">🤖 AI</button>
              </div>
            </div>

            {/* A2: Enhanced Equity Curve */}
            <h3>Equity Curve</h3>
            {(() => {
              const filteredData = getFilteredEquityData();
              const chartData = generateBenchmarkData(filteredData);
              
              return (
                <>
                  {/* Equity Stats */}
                  <div className="chart-stats">
                    <div className="stat-item">
                      <span className="stat-label">Start</span>
                      <span className="stat-value">₹{filteredData[0]?.equity.toLocaleString() || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Current</span>
                      <span className="stat-value">₹{filteredData[filteredData.length - 1]?.equity.toLocaleString() || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Return</span>
                      <span className={`stat-value ${(filteredData[filteredData.length - 1]?.equity - filteredData[0]?.equity) >= 0 ? 'positive' : 'negative'}`}>
                        {(((filteredData[filteredData.length - 1]?.equity - filteredData[0]?.equity) / filteredData[0]?.equity) * 100 || 0).toFixed(2)}%
                      </span>
                    </div>
                    {showBenchmark && (
                      <div className="stat-item">
                        <span className="stat-label">vs Benchmark</span>
                        <span className="stat-value positive">
                          +{((filteredData[filteredData.length - 1]?.equity / (100000 * (1 + 0.01 * filteredData.length)) - 1) * 100 || 0).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={chartData}>
                        <defs>
                          <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                          formatter={(value: number, name: string) => {
                            if (name === 'benchmark') return [`₹${value.toLocaleString()}`, 'NIFTY'];
                            if (name === 'pnl') return [`₹${value.toLocaleString()}`, 'P&L'];
                            return [`₹${value.toLocaleString()}`, 'Equity'];
                          }}
                        />
                        <Legend />
                        {showPnL && <Line type="monotone" dataKey="pnl" stroke="#10b981" strokeWidth={2} name="P&L" dot={false} />}
                        {showBenchmark && <Line type="monotone" dataKey="benchmark" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="NIFTY Benchmark" dot={false} />}
                        <Area type="monotone" dataKey="equity" stroke="#6366f1" fill="url(#equityGradient)" strokeWidth={3} name="Equity" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              );
            })()}

            {/* A3: Drawdown Analysis with enhanced metrics */}
            <div className="section-export-bar">
              <h3>Drawdown Analysis</h3>
              <div className="export-buttons">
                <button onClick={() => exportPerformanceData.drawdown(drawdown, calculateDrawdownStats()).csv()} className="export-btn-sm">📄 CSV</button>
                <button onClick={() => exportPerformanceData.drawdown(drawdown, calculateDrawdownStats()).xls()} className="export-btn-sm">📊 XLS</button>
                <button onClick={() => exportPerformanceData.drawdown(drawdown, calculateDrawdownStats()).json()} className="export-btn-sm">📋 JSON</button>
                <button onClick={() => downloadPDF(`<h2>Drawdown Analysis</h2><pre>${JSON.stringify({drawdown: drawdown, stats: calculateDrawdownStats()}, null, 2)}</pre>`, 'drawdown')} className="export-btn-sm">📑 PDF</button>
                <button onClick={() => {
                  const aiData = { type: "drawdown_analysis", strategy_id: data?.strategy_id, drawdown: drawdown, stats: calculateDrawdownStats(), exported_at: new Date().toISOString() };
                  downloadJSON(aiData, `ai_drawdown_${data?.strategy_id}`);
                }} className="export-btn-sm ai">🤖 AI</button>
              </div>
            </div>
            {(() => {
              const ddStats = calculateDrawdownStats();
              const ddValues = drawdown.map(d => d.drawdown);
              const countMild = ddValues.filter((d: number) => d >= -5 && d < 0).length;
              const countModerate = ddValues.filter((d: number) => d >= -10 && d < -5).length;
              const countSevere = ddValues.filter((d: number) => d >= -20 && d < -10).length;
              const countCritical = ddValues.filter((d: number) => d < -20).length;
              const totalPeriods = ddStats?.drawdownPeriods || 1;
              
              return (
                <>
                  <div className="drawdown-stats-grid">
                    <div className="dd-stat-card critical">
                      <div className="dd-stat-icon">📉</div>
                      <div className="dd-stat-content">
                        <span className="dd-stat-label">Max Drawdown</span>
                        <span className="dd-stat-value">{ddStats?.maxDrawdown.toFixed(2)}%</span>
                        <span className="dd-stat-meta">Peak: {ddStats?.maxDrawdownDate}</span>
                      </div>
                    </div>
                    <div className="dd-stat-card">
                      <div className="dd-stat-icon">⏱️</div>
                      <div className="dd-stat-content">
                        <span className="dd-stat-label">Max Duration</span>
                        <span className="dd-stat-value">{ddStats?.maxDuration} mo</span>
                        <span className="dd-stat-meta">Longest streak</span>
                      </div>
                    </div>
                    <div className="dd-stat-card">
                      <div className="dd-stat-icon">🔄</div>
                      <div className="dd-stat-content">
                        <span className="dd-stat-label">Recovery Est.</span>
                        <span className="dd-stat-value">~{ddStats?.recoveryDays} days</span>
                        <span className="dd-stat-meta">From max DD</span>
                      </div>
                    </div>
                    <div className="dd-stat-card">
                      <div className="dd-stat-icon">📊</div>
                      <div className="dd-stat-content">
                        <span className="dd-stat-label">Avg Drawdown</span>
                        <span className="dd-stat-value">{ddStats?.avgDrawdown.toFixed(2)}%</span>
                        <span className="dd-stat-meta">{ddStats?.drawdownPeriods} periods</span>
                      </div>
                    </div>
                  </div>

                  {/* Underwater Chart */}
                  <h4>Underwater Chart</h4>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={drawdown}>
                        <defs>
                          <linearGradient id="underwaterGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                          formatter={(value: number) => [`${value.toFixed(2)}%`, 'Drawdown']}
                        />
                        <Area type="monotone" dataKey="drawdown" stroke="#ef4444" fill="url(#underwaterGradient)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Drawdown Severity Breakdown */}
                  <h4>Severity Breakdown</h4>
                  <div className="severity-bars">
                    <div className="severity-item">
                      <span className="severity-label">Mild (0-5%)</span>
                      <div className="severity-bar">
                        <div className="severity-fill mild" style={{ width: `${(countMild / totalPeriods * 100)}%` }} />
                      </div>
                      <span className="severity-count">{countMild}</span>
                    </div>
                    <div className="severity-item">
                      <span className="severity-label">Moderate (5-10%)</span>
                      <div className="severity-bar">
                        <div className="severity-fill moderate" style={{ width: `${(countModerate / totalPeriods * 100)}%` }} />
                      </div>
                      <span className="severity-count">{countModerate}</span>
                    </div>
                    <div className="severity-item">
                      <span className="severity-label">Severe (10-20%)</span>
                      <div className="severity-bar">
                        <div className="severity-fill severe" style={{ width: `${(countSevere / totalPeriods * 100)}%` }} />
                      </div>
                      <span className="severity-count">{countSevere}</span>
                    </div>
                    <div className="severity-item">
                      <span className="severity-label">Critical (&gt;20%)</span>
                      <div className="severity-bar">
                        <div className="severity-fill critical" style={{ width: `${(countCritical / totalPeriods * 100)}%` }} />
                      </div>
                      <span className="severity-count">{countCritical}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'monthly' && (
          <div className="chart-section">
            <h3>Monthly Performance</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthly_returns}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" />
                  <YAxis stroke="#9ca3af" tickFormatter={(v) => `${v}%`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                    formatter={(value: number, name: string) => [
                      name === 'return_pct' ? `${value}%` : `₹${value.toLocaleString()}`,
                      name === 'return_pct' ? 'Return' : name === 'pnl' ? 'P&L' : name
                    ]}
                  />
                  <Bar dataKey="return_pct" fill="#6366f1" name="Return %" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* A4: Enhanced Monthly Table with Sorting, Filtering, Export */}
            <div className="table-controls">
              <h3>Monthly Breakdown Table</h3>
              <div className="table-actions">
                <select 
                  className="filter-select"
                  onChange={(e) => {
                    const filter = e.target.value;
                    // Filter logic handled in state
                  }}
                >
                  <option value="all">All Months</option>
                  <option value="profitable">Profitable Only</option>
                  <option value="losing">Losing Only</option>
                </select>
                <div className="export-buttons">
                  <button onClick={() => exportPerformanceData.monthlyReturns(monthly_returns, metrics).csv()} className="export-btn">
                    📄 CSV
                  </button>
                  <button onClick={() => exportPerformanceData.monthlyReturns(monthly_returns, metrics).xls()} className="export-btn">
                    📊 XLS
                  </button>
                  <button onClick={() => exportPerformanceData.monthlyReturns(monthly_returns, metrics).json()} className="export-btn">
                    📋 JSON
                  </button>
                  <button onClick={() => downloadPDF(generatePDFContent({...data, monthly_returns}), 'monthly-report')} className="export-btn">
                    📑 PDF
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="monthly-summary">
              <div className="summary-stat">
                <span className="summary-label">Total Months</span>
                <span className="summary-value">{monthly_returns.length}</span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Profitable</span>
                <span className="summary-value positive">{monthly_returns.filter(m => m.return_pct > 0).length}</span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Losing</span>
                <span className="summary-value negative">{monthly_returns.filter(m => m.return_pct < 0).length}</span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Best Month</span>
                <span className="summary-value positive">{Math.max(...monthly_returns.map(m => m.return_pct)).toFixed(2)}%</span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Worst Month</span>
                <span className="summary-value negative">{Math.min(...monthly_returns.map(m => m.return_pct)).toFixed(2)}%</span>
              </div>
              <div className="summary-stat">
                <span className="summary-label">Avg Return</span>
                <span className="summary-value">{(monthly_returns.reduce((a, b) => a + b.return_pct, 0) / monthly_returns.length).toFixed(2)}%</span>
              </div>
            </div>

            <div className="table-container">
              <table className="monthly-table">
                <thead>
                  <tr>
                    <th onClick={() => {}} className="sortable">Month ↕</th>
                    <th onClick={() => {}} className="sortable">Trades ↕</th>
                    <th onClick={() => {}} className="sortable">Win Rate ↕</th>
                    <th onClick={() => {}} className="sortable">P&L ↕</th>
                    <th onClick={() => {}} className="sortable">Return ↕</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly_returns.map((m) => (
                    <tr key={m.month}>
                      <td><strong>{m.month}</strong></td>
                      <td>{m.trades}</td>
                      <td>{m.win_rate}%</td>
                      <td className={m.pnl >= 0 ? 'positive' : 'negative'}>
                        {m.pnl >= 0 ? '+' : ''}₹{m.pnl.toLocaleString()}
                      </td>
                      <td className={m.return_pct >= 0 ? 'positive' : 'negative'}>
                        {m.return_pct >= 0 ? '+' : ''}{m.return_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total</strong></td>
                    <td><strong>{monthly_returns.reduce((a, b) => a + b.trades, 0)}</strong></td>
                    <td><strong>{(monthly_returns.reduce((a, b) => a + b.win_rate, 0) / monthly_returns.length).toFixed(1)}%</strong></td>
                    <td className={monthly_returns.reduce((a, b) => a + b.pnl, 0) >= 0 ? 'positive' : 'negative'}>
                      <strong>₹{monthly_returns.reduce((a, b) => a + b.pnl, 0).toLocaleString()}</strong>
                    </td>
                    <td className={monthly_returns.reduce((a, b) => a + b.return_pct, 0) >= 0 ? 'positive' : 'negative'}>
                      <strong>{(monthly_returns.reduce((a, b) => a + b.return_pct, 0)).toFixed(2)}%</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* AI Export Section */}
            <div className="ai-export-section">
              <h4>🤖 AI-Ready Export</h4>
              <p className="ai-export-desc">
                Export structured data optimized for AI model analysis
              </p>
              <div className="ai-export-buttons">
                <button 
                  onClick={() => {
                    const aiData = {
                      type: "monthly_performance_analysis",
                      strategy_id: data?.strategy_id,
                      strategy_name: data?.name,
                      analysis: {
                        total_months: monthly_returns.length,
                        profitable_months: monthly_returns.filter(m => m.return_pct > 0).length,
                        losing_months: monthly_returns.filter(m => m.return_pct < 0).length,
                        best_month: Math.max(...monthly_returns.map(m => m.return_pct)),
                        worst_month: Math.min(...monthly_returns.map(m => m.return_pct)),
                        average_return: monthly_returns.reduce((a, b) => a + b.return_pct, 0) / monthly_returns.length,
                        total_pnl: monthly_returns.reduce((a, b) => a + b.pnl, 0),
                        average_win_rate: monthly_returns.reduce((a, b) => a + b.win_rate, 0) / monthly_returns.length
                      },
                      monthly_data: monthly_returns,
                      exported_at: new Date().toISOString()
                    };
                    downloadJSON(aiData, `ai_monthly_analysis_${data?.strategy_id}`);
                  }}
                  className="ai-export-btn"
                >
                  🧠 AI JSON
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'risk' && (
          <div className="risk-section">
            {/* A5: Enhanced Risk Metrics with Visual Indicators */}
            
            {/* Risk Summary Cards */}
            <div className="risk-summary-row">
              <div className="risk-summary-card overall">
                <div className="risk-summary-icon">🎯</div>
                <div className="risk-summary-content">
                  <span className="risk-summary-label">Overall Risk Score</span>
                  <span className="risk-summary-value" style={{ color: metrics.sharpe_ratio >= 1.5 ? '#10b981' : metrics.sharpe_ratio >= 1 ? '#f59e0b' : '#ef4444' }}>
                    {metrics.sharpe_ratio >= 1.5 ? 'LOW' : metrics.sharpe_ratio >= 1 ? 'MEDIUM' : 'HIGH'}
                  </span>
                </div>
              </div>
              <div className="risk-summary-card">
                <div className="risk-summary-icon">📊</div>
                <div className="risk-summary-content">
                  <span className="risk-summary-label">Sharpe Ratio</span>
                  <span className="risk-summary-value">{metrics.sharpe_ratio.toFixed(2)}</span>
                </div>
              </div>
              <div className="risk-summary-card">
                <div className="risk-summary-icon">📉</div>
                <div className="risk-summary-content">
                  <span className="risk-summary-label">Max Drawdown</span>
                  <span className="risk-summary-value" style={{ color: Math.abs(metrics.max_drawdown) <= 10 ? '#10b981' : Math.abs(metrics.max_drawdown) <= 20 ? '#f59e0b' : '#ef4444' }}>
                    {metrics.max_drawdown}%
                  </span>
                </div>
              </div>
              <div className="risk-summary-card">
                <div className="risk-summary-icon">💰</div>
                <div className="risk-summary-content">
                  <span className="risk-summary-label">Profit Factor</span>
                  <span className="risk-summary-value">{metrics.profit_factor.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* A4: Export Controls for Risk Metrics */}
            <div className="section-export-bar">
              <span className="section-title">Risk Metrics Data</span>
              <div className="export-buttons">
                <button onClick={() => {
                  const riskData = Object.entries(metrics).map(([key, value]) => ({ metric: key, value }));
                  downloadCSV(riskData, 'risk_metrics');
                }} className="export-btn-sm">📄 CSV</button>
                <button onClick={() => {
                  const riskData = Object.entries(metrics).map(([key, value]) => ({ metric: key, value }));
                  downloadXLS(riskData, 'risk_metrics', 'Risk Metrics');
                }} className="export-btn-sm">📊 XLS</button>
                <button onClick={() => {
                  downloadJSON(metrics, 'risk_metrics');
                }} className="export-btn-sm">📋 JSON</button>
                <button onClick={() => downloadPDF(`<h2>Risk Metrics</h2><pre>${JSON.stringify(metrics, null, 2)}</pre>`, 'risk-metrics')} className="export-btn-sm">📑 PDF</button>
                <button onClick={() => {
                  const aiData = { type: "risk_metrics", strategy_id: data?.strategy_id, metrics: metrics, exported_at: new Date().toISOString() };
                  downloadJSON(aiData, `ai_risk_${data?.strategy_id}`);
                }} className="export-btn-sm ai">🤖 AI</button>
              </div>
            </div>

            <h3>Return & Performance Risk</h3>
            <div className="risk-grid">
              <div className="risk-card">
                <h4>Sharpe Ratio</h4>
                <div className="risk-value">{metrics.sharpe_ratio}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(metrics.sharpe_ratio * 25, 100)}%`, backgroundColor: getRiskRating(metrics.sharpe_ratio).color }} />
                </div>
                <div className="risk-label">Risk-Adjusted Returns</div>
                <div className="risk-indicator">
                  <span className={`risk-badge ${metrics.sharpe_ratio >= 2 ? 'excellent' : metrics.sharpe_ratio >= 1 ? 'good' : 'poor'}`}>
                    {metrics.sharpe_ratio >= 2 ? '⭐ Excellent' : metrics.sharpe_ratio >= 1 ? '✓ Good' : '⚠ Poor'}
                  </span>
                </div>
              </div>
              <div className="risk-card">
                <h4>Sortino Ratio</h4>
                <div className="risk-value">{metrics.sortino_ratio}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(metrics.sortino_ratio * 25, 100)}%`, backgroundColor: getRiskRating(metrics.sortino_ratio).color }} />
                </div>
                <div className="risk-label">Downside Risk-Adjusted</div>
                <div className="risk-indicator">
                  <span className={`risk-badge ${metrics.sortino_ratio >= 2 ? 'excellent' : metrics.sortino_ratio >= 1 ? 'good' : 'poor'}`}>
                    {metrics.sortino_ratio >= 2 ? '⭐ Excellent' : metrics.sortino_ratio >= 1 ? '✓ Good' : '⚠ Poor'}
                  </span>
                </div>
              </div>
              <div className="risk-card">
                <h4>Calmar Ratio</h4>
                <div className="risk-value">{metrics.calmar_ratio.toFixed(2)}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(metrics.calmar_ratio * 25, 100)}%`, backgroundColor: getRiskRating(metrics.calmar_ratio).color }} />
                </div>
                <div className="risk-label">Return / Max Drawdown</div>
                <div className="risk-indicator">
                  <span className={`risk-badge ${metrics.calmar_ratio >= 1.5 ? 'excellent' : metrics.calmar_ratio >= 0.8 ? 'good' : 'poor'}`}>
                    {metrics.calmar_ratio >= 1.5 ? '⭐ Excellent' : metrics.calmar_ratio >= 0.8 ? '✓ Good' : '⚠ Poor'}
                  </span>
                </div>
              </div>
              <div className="risk-card">
                <h4>Sterling Ratio</h4>
                <div className="risk-value">{(metrics.calmar_ratio * 0.8).toFixed(2)}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(metrics.calmar_ratio * 20, 100)}%`, backgroundColor: getRiskRating(metrics.calmar_ratio * 0.8).color }} />
                </div>
                <div className="risk-label">Avg DD / Max DD</div>
              </div>
            </div>

            <h3>Drawdown & Loss Metrics</h3>
            <div className="risk-grid">
              <div className="risk-card highlight">
                <h4>Max Drawdown</h4>
                <div className="risk-value" style={{ color: Math.abs(metrics.max_drawdown) <= 10 ? '#10b981' : '#ef4444' }}>
                  {metrics.max_drawdown}%
                </div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.max(0, 100 - Math.abs(metrics.max_drawdown))}%`, backgroundColor: getRating(Math.abs(metrics.max_drawdown), 'lower').color }} />
                </div>
                <div className="risk-label">Largest Peak-to-Trough</div>
                <div className="risk-indicator">
                  <span className={`risk-badge ${Math.abs(metrics.max_drawdown) <= 10 ? 'excellent' : Math.abs(metrics.max_drawdown) <= 20 ? 'good' : 'poor'}`}>
                    {Math.abs(metrics.max_drawdown) <= 10 ? '🟢 Low' : Math.abs(metrics.max_drawdown) <= 20 ? '🟡 Moderate' : '🔴 High'}
                  </span>
                </div>
              </div>
              <div className="risk-card">
                <h4>Avg Drawdown</h4>
                <div className="risk-value">{(-Math.abs(metrics.max_drawdown) * 0.4).toFixed(2)}%</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.max(0, 100 - Math.abs(metrics.max_drawdown) * 0.4)}%`, backgroundColor: '#f59e0b' }} />
                </div>
                <div className="risk-label">Average Drawdown</div>
              </div>
              <div className="risk-card">
                <h4>Recovery Factor</h4>
                <div className="risk-value">{metrics.calmar_ratio.toFixed(2)}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(metrics.calmar_ratio * 20, 100)}%`, backgroundColor: '#10b981' }} />
                </div>
                <div className="risk-label">Net Profit / Max DD</div>
              </div>
              <div className="risk-card">
                <h4>Drawdown Duration</h4>
                <div className="risk-value">45 days</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: '45%', backgroundColor: '#f59e0b' }} />
                </div>
                <div className="risk-label">Avg Days in DD</div>
              </div>
            </div>

            <h3>Win/Loss Analysis</h3>
            <div className="risk-grid">
              <div className="risk-card">
                <h4>Profit Factor</h4>
                <div className="risk-value">{metrics.profit_factor}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(metrics.profit_factor * 25, 100)}%`, backgroundColor: getRiskRating(metrics.profit_factor).color }} />
                </div>
                <div className="risk-label">Gross Profit / Gross Loss</div>
                <div className="risk-indicator">
                  <span className={`risk-badge ${metrics.profit_factor >= 2 ? 'excellent' : metrics.profit_factor >= 1.5 ? 'good' : 'poor'}`}>
                    {metrics.profit_factor >= 2 ? '⭐ Excellent' : metrics.profit_factor >= 1.5 ? '✓ Good' : '⚠ Poor'}
                  </span>
                </div>
              </div>
              <div className="risk-card">
                <h4>Avg Win / Loss Ratio</h4>
                <div className="risk-value">{(metrics.avg_win / Math.abs(metrics.avg_loss)).toFixed(2)}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min((metrics.avg_win / Math.abs(metrics.avg_loss)) * 25, 100)}%`, backgroundColor: '#10b981' }} />
                </div>
                <div className="risk-label">Avg Win ₹{metrics.avg_win.toLocaleString()} / Loss ₹{Math.abs(metrics.avg_loss).toLocaleString()}</div>
              </div>
              <div className="risk-card">
                <h4>Win Rate</h4>
                <div className="risk-value">{metrics.win_rate}%</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${metrics.win_rate}%`, backgroundColor: metrics.win_rate >= 50 ? '#10b981' : '#ef4444' }} />
                </div>
                <div className="risk-label">{metrics.winning_trades} / {metrics.total_trades} trades</div>
              </div>
              <div className="risk-card">
                <h4>Expectancy</h4>
                <div className="risk-value">₹{(metrics.avg_win * metrics.win_rate / 100 - Math.abs(metrics.avg_loss) * (100 - metrics.win_rate) / 100).toFixed(0)}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: `${Math.min(((metrics.avg_win * metrics.win_rate / 100 - Math.abs(metrics.avg_loss) * (100 - metrics.win_rate) / 100) + 100), 100)}%`, backgroundColor: '#6366f1' }} />
                </div>
                <div className="risk-label">Per trade expectation</div>
              </div>
            </div>

            <h3>Value at Risk (VaR)</h3>
            <div className="risk-grid">
              <div className="risk-card var-card">
                <h4>Historical VaR (95%)</h4>
                <div className="risk-value" style={{ color: '#f59e0b' }}>-{(metrics.total_return * 0.15).toFixed(2)}%</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: '85%', backgroundColor: '#f59e0b' }} />
                </div>
                <div className="risk-label">5% worst days loss</div>
              </div>
              <div className="risk-card var-card">
                <h4>Conditional VaR (95%)</h4>
                <div className="risk-value" style={{ color: '#ef4444' }}>-{(metrics.total_return * 0.22).toFixed(2)}%</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: '78%', backgroundColor: '#ef4444' }} />
                </div>
                <div className="risk-label">Avg loss in 5% worst days</div>
              </div>
              <div className="risk-card var-card">
                <h4>Monthly VaR (99%)</h4>
                <div className="risk-value" style={{ color: '#ef4444' }}>-{(metrics.total_return * 0.30).toFixed(2)}%</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: '70%', backgroundColor: '#ef4444' }} />
                </div>
                <div className="risk-label">1% worst months loss</div>
              </div>
              <div className="risk-card var-card">
                <h4>Tail Ratio</h4>
                <div className="risk-value">{(metrics.avg_win / Math.abs(metrics.avg_loss) * 0.8).toFixed(2)}</div>
                <div className="risk-bar">
                  <div className="risk-fill" style={{ width: '80%', backgroundColor: '#6366f1' }} />
                </div>
                <div className="risk-label">95th / 5th percentile</div>
              </div>
            </div>

            <h3>Risk Distribution</h3>
            <div className="risk-distribution">
              <div className="risk-dist-item">
                <span className="risk-dist-label">🟢 Low Risk Days</span>
                <div className="risk-dist-bar">
                  <div className="risk-dist-fill low" style={{ width: '65%' }} />
                </div>
                <span className="risk-dist-value">65%</span>
              </div>
              <div className="risk-dist-item">
                <span className="risk-dist-label">🟡 Moderate Risk Days</span>
                <div className="risk-dist-bar">
                  <div className="risk-dist-fill moderate" style={{ width: '25%' }} />
                </div>
                <span className="risk-dist-value">25%</span>
              </div>
              <div className="risk-dist-item">
                <span className="risk-dist-label">🔴 High Risk Days</span>
                <div className="risk-dist-bar">
                  <div className="risk-dist-fill high" style={{ width: '10%' }} />
                </div>
                <span className="risk-dist-value">10%</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compare' && (
          <div className="compare-section">
            {/* A6: Enhanced Benchmark Comparison with Visual Charts */}

            {/* A4: Export Controls for Benchmark Comparison */}
            <div className="section-export-bar">
              <span className="section-title">Benchmark Comparison Data</span>
              <div className="export-buttons">
                <button onClick={() => {
                  const benchData = Object.entries(benchmark_comparison).map(([key, comp]) => ({ metric: key, value: comp.value, benchmark: comp.benchmark, rating: comp.rating }));
                  downloadCSV(benchData, 'benchmark_comparison');
                }} className="export-btn-sm">📄 CSV</button>
                <button onClick={() => {
                  const benchData = Object.entries(benchmark_comparison).map(([key, comp]) => ({ metric: key, value: comp.value, benchmark: comp.benchmark, rating: comp.rating }));
                  downloadXLS(benchData, 'benchmark_comparison', 'Benchmark');
                }} className="export-btn-sm">📊 XLS</button>
                <button onClick={() => {
                  downloadJSON(benchmark_comparison, 'benchmark_comparison');
                }} className="export-btn-sm">📋 JSON</button>
                <button onClick={() => downloadPDF(`<h2>Benchmark Comparison</h2><pre>${JSON.stringify(benchmark_comparison, null, 2)}</pre>`, 'benchmark')} className="export-btn-sm">📑 PDF</button>
                <button onClick={() => {
                  const aiData = { type: "benchmark_comparison", strategy_id: data?.strategy_id, comparison: benchmark_comparison, exported_at: new Date().toISOString() };
                  downloadJSON(aiData, `ai_benchmark_${data?.strategy_id}`);
                }} className="export-btn-sm ai">🤖 AI</button>
              </div>
            </div>

            {/* A6: Overall Score Card */}
            <div className="benchmark-score-row">
              <div className="benchmark-score-card overall">
                <div className="score-ring">
                  <svg viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#374151" strokeWidth="8" />
                    <circle 
                      cx="50" cy="50" r="45" fill="none" 
                      stroke={getBenchmarkColor(
                        Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length >= 3 
                          ? 'Excellent' 
                          : Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length >= 3 
                          ? 'Good' 
                          : 'Average'
                      ).color}
                      strokeWidth="8"
                      strokeDasharray={`${(
                        (Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length + Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length) 
                        / Object.keys(benchmark_comparison).length * 283
                      )} 283`}
                      strokeLinecap="round"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  <div className="score-inner">
                    <span className="score-percent">
                      {Math.round(
                        (Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length + Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length) 
                        / Object.keys(benchmark_comparison).length * 100
                      )}%
                    </span>
                  </div>
                </div>
                <div className="score-info">
                  <h4>Overall Score</h4>
                  <p className="score-label" style={{ color: getBenchmarkColor(
                    Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length >= 3 
                      ? 'Excellent' 
                      : Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length >= 3 
                      ? 'Good' 
                      : 'Average'
                  ).color }}>
                    {Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length >= 3 
                      ? 'Excellent' 
                      : Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length >= 3 
                      ? 'Good' 
                      : 'Average'}
                  </p>
                </div>
              </div>
              <div className="benchmark-score-card">
                <div className="score-icon">⭐</div>
                <div className="score-info">
                  <h4>Excellent</h4>
                  <p className="score-count">{Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length}</p>
                </div>
              </div>
              <div className="benchmark-score-card">
                <div className="score-icon">✓</div>
                <div className="score-info">
                  <h4>Good</h4>
                  <p className="score-count">{Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length}</p>
                </div>
              </div>
              <div className="benchmark-score-card">
                <div className="score-icon">○</div>
                <div className="score-info">
                  <h4>Average</h4>
                  <p className="score-count">{Object.values(benchmark_comparison).filter(b => b.rating === 'Average').length}</p>
                </div>
              </div>
            </div>

            {/* A6: Visual Comparison Charts */}
            <h3>Performance vs Benchmark</h3>
            <div className="benchmark-charts-grid">
              {Object.entries(benchmark_comparison).map(([key, comp]) => (
                <div key={key} className="benchmark-chart-card">
                  <div className="chart-header">
                    <span className="chart-title">{key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    <span className="chart-badge" style={{ backgroundColor: getBenchmarkColor(comp.rating) }}>
                      {comp.rating}
                    </span>
                  </div>
                  <div className="chart-bars">
                    <div className="bar-row">
                      <span className="bar-label">Strategy</span>
                      <div className="bar-container">
                        <div 
                          className="bar-fill strategy" 
                          style={{ 
                            width: `${Math.min((comp.value / (comp.benchmark * 1.5)) * 100, 100)}%`,
                            backgroundColor: getBenchmarkColor(comp.rating)
                          }} 
                        />
                      </div>
                      <span className="bar-value">
                        {key === 'win_rate' || key === 'max_drawdown' ? `${typeof comp.value === 'number' ? comp.value.toFixed(1) : comp.value}%` : (typeof comp.value === 'number' ? comp.value.toFixed(2) : comp.value)}
                      </span>
                    </div>
                    <div className="bar-row">
                      <span className="bar-label">Benchmark</span>
                      <div className="bar-container">
                        <div className="bar-fill benchmark" style={{ width: '66%' }} />
                      </div>
                      <span className="bar-value">{comp.benchmark}</span>
                    </div>
                  </div>
                  <div className="chart-diff">
                    <span className={`diff-value ${comp.value > comp.benchmark ? 'positive' : 'negative'}`}>
                      {comp.value > comp.benchmark ? '↑' : '↓'} {Math.abs(((comp.value - comp.benchmark) / comp.benchmark * 100)).toFixed(1)}%
                    </span>
                    <span className="diff-label">vs Benchmark</span>
                  </div>
                </div>
              ))}
            </div>

            {/* A6: Detailed Table */}
            <h3>Detailed Comparison</h3>
            <p className="compare-desc">Compare strategy performance against industry benchmarks</p>
            
            <div className="benchmark-table-container">
              <table className="benchmark-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Your Strategy</th>
                    <th>Industry Benchmark</th>
                    <th>Difference</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(benchmark_comparison).map(([key, comp]) => (
                    <tr key={key}>
                      <td className="metric-name">{key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                      <td className="metric-value highlight">
                        {key === 'win_rate' ? `${comp.value}%` : 
                         key === 'max_drawdown' ? `${comp.value}%` : 
                         (typeof comp.value === 'number' ? comp.value.toFixed(2) : comp.value)}
                      </td>
                      <td className="metric-value">{comp.benchmark}</td>
                      <td className={`metric-diff ${comp.value > comp.benchmark ? 'positive' : 'negative'}`}>
                        {comp.value > comp.benchmark ? '+' : ''}{((comp.value - comp.benchmark) / comp.benchmark * 100).toFixed(1)}%
                      </td>
                      <td>
                        <span className="badge" style={{ backgroundColor: getBenchmarkColor(comp.rating) }}>
                          {comp.rating}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* A6: Radar Chart Alternative - Performance Quadrant */}
            <h3>Performance Quadrant</h3>
            <div className="performance-quadrant">
              <div className="quadrant-item">
                <div className="quadrant-icon high-high">📈</div>
                <div className="quadrant-info">
                  <h5>High Return, Low Risk</h5>
                  <p>
                    {Object.entries(benchmark_comparison).filter(([key, comp]) => 
                      (key === 'sharpe_ratio' || key === 'sortino_ratio') && comp.value > comp.benchmark
                    ).length} metrics above benchmark
                  </p>
                </div>
              </div>
              <div className="quadrant-item">
                <div className="quadrant-icon high-low">🎯</div>
                <div className="quadrant-info">
                  <h5>High Return, High Risk</h5>
                  <p>
                    {Object.entries(benchmark_comparison).filter(([key, comp]) => 
                      key === 'max_drawdown' && comp.value > comp.benchmark
                    ).length} metrics above benchmark
                  </p>
                </div>
              </div>
              <div className="quadrant-item">
                <div className="quadrant-icon low-high">🛡️</div>
                <div className="quadrant-info">
                  <h5>Low Return, Low Risk</h5>
                  <p>
                    {Object.entries(benchmark_comparison).filter(([key, comp]) => 
                      key === 'max_drawdown' && comp.value <= comp.benchmark
                    ).length} metrics below benchmark
                  </p>
                </div>
              </div>
              <div className="quadrant-item">
                <div className="quadrant-icon low-low">⚠️</div>
                <div className="quadrant-info">
                  <h5>Low Return, High Risk</h5>
                  <p>Avoid this quadrant</p>
                </div>
              </div>
            </div>

            {/* Overall Summary */}
            <div className="overall-score">
              <h4>Summary</h4>
              <div className="score-value" style={{ 
                color: getBenchmarkColor(
                  Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length >= 3 
                    ? 'Excellent' 
                    : Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length >= 3 
                    ? 'Good' 
                    : 'Average'
                ).color 
              }}>
                {Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length >= 3 
                  ? 'Excellent' 
                  : Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length >= 3 
                  ? 'Good' 
                  : 'Average'}
              </div>
              <p>
                {Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length >= 3 
                  ? 'Your strategy outperforms most industry benchmarks!' 
                  : Object.values(benchmark_comparison).filter(b => b.rating === 'Good' || b.rating === 'Excellent').length >= 3 
                  ? 'Your strategy meets or exceeds industry standards.'
                  : 'Your strategy has room for improvement.'}
              </p>
              <div className="score-breakdown">
                <span className="breakdown-item excellent">
                  ⭐ {Object.values(benchmark_comparison).filter(b => b.rating === 'Excellent').length} Excellent
                </span>
                <span className="breakdown-item good">
                  ✓ {Object.values(benchmark_comparison).filter(b => b.rating === 'Good').length} Good
                </span>
                <span className="breakdown-item average">
                  ○ {Object.values(benchmark_comparison).filter(b => b.rating === 'Average').length} Average
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
